import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Eye,
  FileSpreadsheet,
  FileText,
  History,
  Landmark,
  Trash2,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise } from '@/lib/locale'
import {
  useDeleteHistoryOne,
  useDeleteHistorySource,
  useHistory,
  useHistoryTransactions,
  useRequestHistoryDeleteCode,
} from '@/features/dataHistory/queries'
import type {
  HistoryEntry,
  HistorySource,
  HistoryTransaction,
} from '@/features/dataHistory/types'

const SOURCES: { value: HistorySource; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'plaid', label: 'Plaid', icon: Landmark },
  { value: 'pdf', label: 'PDF', icon: FileText },
  { value: 'csv', label: 'CSV', icon: FileSpreadsheet },
]

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return '-'
  return formatCurrencyPrecise(n)
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return String(v)
  }
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleString()
  } catch {
    return String(v)
  }
}

type ModalViewTarget =
  | { kind: 'file'; historyId: string; source: HistorySource }
  | null

type DeleteTarget =
  | { kind: 'one'; id: number }
  | { kind: 'all'; source: HistorySource }
  | null

export function DataHistoryPage() {
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSource = searchParams.get('source') as HistorySource | null
  const urlFileId = searchParams.get('viewFileId')

  const [source, setSource] = useState<HistorySource>(
    urlSource && ['plaid', 'pdf', 'csv'].includes(urlSource) ? urlSource : 'plaid',
  )
  const [viewTarget, setViewTarget] = useState<ModalViewTarget>(null)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null)
  const [deleteCode, setDeleteCode] = useState('')
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  const requestCodeMut = useRequestHistoryDeleteCode()
  const deleteOneMut = useDeleteHistoryOne()
  const deleteSourceMut = useDeleteHistorySource()

  const q = useHistory(source)

  // Deep-link: open the transactions modal after the list loads
  useEffect(() => {
    if (urlFileId && source === (urlSource ?? 'plaid')) {
      setViewTarget({ kind: 'file', historyId: urlFileId, source })
      // Clear the query params so refresh doesn't re-open
      const next = new URLSearchParams(searchParams)
      next.delete('viewFileId')
      next.delete('source')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const beginDelete = async (target: Exclude<DeleteTarget, null>) => {
    try {
      await requestCodeMut.mutateAsync()
      setDeleteTarget(target)
      setDeleteCode('')
      setDeleteErr(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not send code',
        description: errorMessage(err),
      })
    }
  }

  const submitDelete = async () => {
    if (!deleteTarget) return
    if (!deleteCode.trim()) {
      setDeleteErr('Please enter the verification code.')
      return
    }
    try {
      if (deleteTarget.kind === 'one') {
        await deleteOneMut.mutateAsync({
          id: deleteTarget.id,
          payload: { verificationCode: deleteCode.trim() },
        })
      } else {
        await deleteSourceMut.mutateAsync({
          source: deleteTarget.source,
          payload: { verificationCode: deleteCode.trim() },
        })
      }
      toast({ title: 'Deleted' })
      setDeleteTarget(null)
    } catch (err) {
      setDeleteErr(errorMessage(err, 'Deletion failed.'))
    }
  }

  const entries = useMemo(() => q.data ?? [], [q.data])

  return (
    <Page
      title="Data ingestion history"
      description="Review every Plaid sync, PDF upload, or CSV import and the transactions it produced."
      width="wide"
      actions={
        <Button
          variant="outline"
          onClick={() => beginDelete({ kind: 'all', source })}
          disabled={requestCodeMut.isPending || entries.length === 0}
        >
          <Trash2 className="size-4" /> Delete all {source.toUpperCase()}
        </Button>
      }
    >
      <Toolbar>
        {SOURCES.map((s) => {
          const Icon = s.icon
          return (
            <Button
              key={s.value}
              variant={source === s.value ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setSource(s.value)}
            >
              <Icon className="size-4" /> {s.label}
            </Button>
          )
        })}
      </Toolbar>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="py-16">
              <LoadingSpinner />
            </div>
          ) : q.isError ? (
            <Alert variant="destructive" className="m-4">
              <AlertDescription>Could not load history: {errorMessage(q.error)}</AlertDescription>
            </Alert>
          ) : entries.length === 0 ? (
            <EmptyState
              icon={History}
              title={`No ${source.toUpperCase()} history yet`}
              description={
                source === 'plaid'
                  ? 'Connect a bank account in Banking to start syncing.'
                  : 'Upload a statement to see it appear here.'
              }
            />
          ) : (
            <>
              {/* md+: DataTable */}
              <div className="hidden md:block">
                <DataTable
                  sortable={false}
                  rows={entries}
                  columns={[
                    {
                      header: 'Date',
                      accessorKey: 'fetch_timestamp',
                      cell: ({ row }) => (
                        <span className="whitespace-nowrap">
                          {fmtDateTime(row.original.fetch_timestamp)}
                        </span>
                      ),
                    },
                    {
                      header: source === 'plaid' ? 'Institution' : 'Original filename',
                      accessorKey: 'details',
                      cell: ({ row }) => {
                        const d = row.original.details
                        const main = source === 'plaid' ? d.institution_name : d.original_filename
                        return (
                          <span className="block max-w-[320px] truncate">{main || '-'}</span>
                        )
                      },
                    },
                    {
                      header: () => <span className="block text-right">Transactions</span>,
                      accessorKey: 'details',
                      id: 'count',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {row.original.details.transaction_count ?? 0}
                        </span>
                      ),
                    },
                    {
                      id: 'actions',
                      header: () => <span className="block text-right">Actions</span>,
                      cell: ({ row }) => (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setViewTarget({
                                kind: 'file',
                                historyId: String(row.original.id),
                                source,
                              })
                            }
                            title="View transactions"
                          >
                            <Eye className="size-4" /> View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => beginDelete({ kind: 'one', id: row.original.id })}
                            disabled={requestCodeMut.isPending}
                            title="Delete this record"
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
              {/* xs/sm: cards */}
              <div className="divide-y md:hidden">
                {entries.map((e) => (
                  <HistoryCard
                    key={e.id}
                    entry={e}
                    source={source}
                    onView={() =>
                      setViewTarget({
                        kind: 'file',
                        historyId: String(e.id),
                        source,
                      })
                    }
                    onDelete={() => beginDelete({ kind: 'one', id: e.id })}
                    deleteBusy={requestCodeMut.isPending}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <TransactionsModal target={viewTarget} onClose={() => setViewTarget(null)} />

      <Modal
        open={deleteTarget !== null}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title={
          deleteTarget?.kind === 'all'
            ? `Delete all ${deleteTarget.source.toUpperCase()} history?`
            : 'Delete history record?'
        }
        description="Enter the 6-digit code we just emailed you."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteOneMut.isPending || deleteSourceMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={submitDelete}
              disabled={deleteOneMut.isPending || deleteSourceMut.isPending}
            >
              {deleteOneMut.isPending || deleteSourceMut.isPending
                ? 'Deleting…'
                : 'Confirm delete'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <Input
            autoFocus
            maxLength={6}
            placeholder="123456"
            value={deleteCode}
            onChange={(e) => {
              setDeleteCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))
              setDeleteErr(null)
            }}
            className="text-center text-lg tracking-[0.4em]"
          />
          {deleteErr ? (
            <p className="text-sm text-destructive">{deleteErr}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {deleteTarget?.kind === 'all'
                ? `Deletes every ${deleteTarget.source.toUpperCase()} history record and all associated transactions.`
                : 'Deletes this history record and all transactions it produced.'}
            </p>
          )}
        </div>
      </Modal>
    </Page>
  )
}

function HistoryCard({
  entry,
  source,
  onView,
  onDelete,
  deleteBusy,
}: {
  entry: HistoryEntry
  source: HistorySource
  onView: () => void
  onDelete: () => void
  deleteBusy: boolean
}) {
  const main =
    source === 'plaid' ? entry.details.institution_name : entry.details.original_filename
  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{main || '-'}</p>
          <p className="text-xs text-muted-foreground">{fmtDateTime(entry.fetch_timestamp)}</p>
        </div>
        <Badge variant="secondary">{entry.details.transaction_count ?? 0} txns</Badge>
      </div>
      <Toolbar align="end" className="mt-2">
        <Button size="sm" variant="ghost" onClick={onView}>
          <Eye className="size-4" /> View
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} disabled={deleteBusy}>
          <Trash2 className="size-4 text-destructive" /> Delete
        </Button>
      </Toolbar>
    </div>
  )
}

function TransactionsModal({
  target,
  onClose,
}: {
  target: ModalViewTarget
  onClose: () => void
}) {
  const q = useHistoryTransactions(
    target?.kind === 'file' ? target.source : null,
    target?.kind === 'file' ? target.historyId : null,
  )

  return (
    <Modal
      open={target !== null}
      onOpenChange={(v) => !v && onClose()}
      title="Associated transactions"
      className="max-w-3xl"
    >
      {q.isLoading ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : q.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Could not load transactions: {errorMessage(q.error)}</AlertDescription>
        </Alert>
      ) : (q.data ?? []).length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No transactions found for this record.</p>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <DataTable
            sortable={false}
            rows={(q.data ?? []) as HistoryTransaction[]}
            columns={[
              {
                header: 'Date',
                accessorKey: 'transaction_date',
                cell: ({ row }) => (
                  <span className="whitespace-nowrap">{fmtDate(row.original.transaction_date)}</span>
                ),
              },
              {
                header: 'Details',
                accessorKey: 'transaction_details',
                cell: ({ row }) => (
                  <span className="block max-w-[320px] truncate">
                    {row.original.transaction_details || '-'}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Amount</span>,
                accessorKey: 'amount',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.amount)}
                  </span>
                ),
              },
              {
                header: 'Category',
                accessorKey: 'category',
                cell: ({ row }) => row.original.category || '-',
              },
            ]}
          />
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  )
}
